import React from 'react';
import {Site, SiteData} from "types/Site";
import {FormikContextType, FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import Form, {
  EmailField,
  FormBlock,
  FormSection,
  MediaField,
  MultiSelectField,
  SelectField,
  TextAreaField,
  TextField
} from "components/Form/Form";
import AccessibilityCriterion from "types/AccessibilityCriterion";
import {toTitleCase} from "services/string";
import IconName from "types/IconName";
import {MapEditor} from "components/MapEditor";
import {Feature, FeatureCollection, Polygon} from "geojson";
import {InformationListField} from "components/Form/Form/ListField";
import {AddressFieldSet, alwaysOpen, OpeningHoursFieldSet} from 'components/Form/Form/Fieldset';
import LevelListField from "./LevelListField";
import {ExclamationCircleIcon, PencilIcon} from "@heroicons/react/solid";
import {getTimeZones} from "@vvo/tzdb";
import {ToggleSwitch} from "components/Form/Form/Control";
import LinkEditor from "components/Form/LinkEditor";
import {useAuth} from "../../../context/AuthContext";
import {feature, featureCollection} from "../../../services/geometry";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {urlTest} from "../../../services/validation-schemas";
import FieldLabel from "../../../components/Form/Form/Field/FieldLabel";

// Types ----

type SiteFormProps = {
  site?: Site
  onSubmit: (values: SiteData, helpers: FormikHelpers<SiteData>) => Promise<any>
}


// Helpers ----

function accessibilityOptions() {
  return Object.values(AccessibilityCriterion).map(id => ({
    id,
    name: toTitleCase(id),
    icon: IconName.HAND
  }))
}

function siteAreaFeatures(siteUuid?: string, area?: Polygon | null): FeatureCollection {
  const features = [];

  if(area) features.push(feature(siteUuid ?? '', {}, area))

  return featureCollection(features);
}

const allTimeZones = getTimeZones({includeUtc: true});

const timezoneOptions = allTimeZones.map(timezone => ({
  id: timezone.name === 'Etc/UTC' ? 'UTC' : timezone.name,
  name: timezone.currentTimeFormat
}))

// Component ----

function SiteForm({site, onSubmit}: SiteFormProps) {
  const {termFor} = useOperator();
  const auth = useAuth();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    about: Yup.string().max(50000, 'Maximum character limit exceeded'),
    links: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().required('This field is required').max(100, 'Maximum 100 characters'),
        url: Yup.string()
          .required('This field is required')
          .max(250, 'Maximum 250 characters')
          .test('valid-url', urlTest),
      })
    ),
    email: Yup.string().email('Email address is not valid').max(255, 'Maximum of 255 characters'),
    phone: Yup.string().max(15, 'Maximum of 15 characters'),
    address: Yup.object().shape({
      line1: Yup.string().required('This field is required').max(250, 'Maximum of 250 characters'),
      line2: Yup.string().max(250, 'Maximum of 250 characters'),
      line3: Yup.string().max(250, 'Maximum of 250 characters'),
      city: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
      postcode: Yup.string().required('This field is required').max(20, 'Maximum of 20 characters'),
      country: Yup.string().required('This field is required'),
    }),
    timezone: Yup.string().required('This field is required'),
    area: Yup.object().shape({
      coordinates: Yup.array().of(
        Yup.array().of(
          Yup.array().of(Yup.number())
        )
      ).required(`Please draw the ${termFor("site")} area on the map using the drawing tool in the top left corner`),
    }),
    levels: Yup.array().of(
      Yup.object().shape({
        number: Yup.number()
          .required('This field is required')
          .min(1, 'Must be a minimum of 1')
          .max(255, 'Must be a maximum of 255'),
        name: Yup.string().required('This field is required').max(250, "Maximum of 250 characters"),
        area: Yup.object().shape({
          coordinates: Yup.array().of(
            Yup.array().of(
              Yup.array().of(Yup.number())
            )
          ).required(`Please draw the level area on the map using the drawing tool in the top left corner`),
        }),
      }
    )),
    visitor_information: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('This field is required').max(250, "Maximum of 250 characters"),
        content: Yup.string().required('This field is required').max(20000, "Maximum character limit exceeded"),
        links: Yup.array().of(
          Yup.object().shape({
            text: Yup.string().required('This field is required'),
            url: Yup.string()
              .test(
                'valid-url',
                urlTest
              )
          })
        ),
      })),
    welcome: Yup.string().max(255, 'Maximum of 255 characters'),
  })

  const initialValues: SiteData = auth.can('sites:write') ? {
    name: site?.name || '',
    about: site?.about || '',
    accessibility_criteria: site?.accessibility_criteria || [],
    image_id: site?.image?.id || undefined,
    email: site?.email || '',
    phone: site?.phone || '',
    address: {
      line1: site?.address?.line1 || '',
      line2: site?.address?.line2 || '',
      line3: site?.address?.line3 || '',
      city: site?.address?.city || '',
      postcode: site?.address?.postcode || '',
      country: site?.address?.country || '',
    },
    timezone: site?.timezone || '',
    area: site?.area || undefined,
    levels: site?.levels.map(level => ({
      id: level.id,
      number: level.number,
      name: level.name,
      description: level.description || '',
      accessibility_criteria: level.accessibility_criteria,
      area: level.area
    })) || [],
    hours: site?.hours ?? alwaysOpen,
    welcome: site?.welcome || '',
    visitor_information: site?.visitor_information.map(info => ({
      id: info.id,
      name: info.name,
      content: info.content,
      links: info.links?.map(link => ({
        id: link.id,
        text: link.text,
        url: link.url,
        category: link.category,
      })) ?? [],
      order: info.order,
    })) ?? [],
    links: site?.links.map(link => ({
      id: link.id,
      text: link.text,
      url: link.url,
      category: link.category,
    })) ?? [],
  } : {
    hours: site?.hours ?? alwaysOpen,
  }

  function handleFeaturesChanged(allFeatures: Array<Feature>, form: FormikContextType<SiteData>) {
    const area = allFeatures.find(feature => feature.geometry.type === 'Polygon')?.geometry ?? null;

    void form.setFieldValue('area', area);

    // Add a ground level if there are no levels added yet
    if (area && Number(form.values.levels?.length) < 1) {
      void form.setFieldValue('levels', [{
        number: 1,
        name: "Ground",
        description: '',
        accessibility_criteria: [],
        area: area,
      }])
    }
  }

  function handleSubmit(values: SiteData, helpers: FormikHelpers<SiteData>) {
    return onSubmit(values, helpers)
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {auth.can('sites:write') &&
        <FormSection
          name="Key Information"
          description={`Basic details about the ${termFor('site')}`}
          columns={6}
        >

          {/* Name */}
          <FormBlock columns={3}>
            <TextField
              name="name"
              label="Name *"
              hint="255 chars max"
              help={`A recognisable name for this ${termFor('site')}`}
              autoComplete="off"
              maxLength={255}
            />
          </FormBlock>

          {/* About */}
          <FormBlock columns={6}>
            <TextAreaField
              name="about"
              label="About"
              hint="Optional. 50,000 chars max."
              help={`General descriptive information about this ${termFor('site')}`}
              autoComplete="off"
              maxChars={50000}
              rows={6}
            />
          </FormBlock>

          {/* Accessibility */}
          <FormBlock columns={6} extraLargeColumns={6}>
            <MultiSelectField
              name="accessibility_criteria"
              label="Accessibility"
              options={accessibilityOptions()}
            />
          </FormBlock>

          {/* Link */}
          <FormBlock columns={6}>
            {form => {
              return (
              <>
                <ToggleSwitch
                  label="Link"
                  checked={!!form.values.links?.length}
                  onChange={enabled => form.setFieldValue('links', enabled ? [{
                    text: '',
                    url: ''
                  }] : [])}
                />

                {!!form.values.links?.length &&
                  <div className="mt-2">
                    <LinkEditor name="links.0"/>
                  </div>
                }
              </>
            )}}
          </FormBlock>
        </FormSection>
      }

      {auth.can('sites:write') &&
        <FormSection
          name="Contact Details"
          description={`Details that can be used to get in touch with staff at the ${termFor('site')}`}
          columns={6}
        >
          {/* Email */}
          <FormBlock columns={3}>
            <EmailField
              name="email"
              label="Email Address"
              hint="255 chars max"
              autoComplete="off"
              maxLength={255}
            />
          </FormBlock>

          {/* Phone */}
          <FormBlock columns={3}>
            <TextField
              name="phone"
              label="Phone Number"
              hint="15 chars max"
              autoComplete="off"
              maxLength={15}
            />
          </FormBlock>

          {/* Street Address */}
          <FormBlock columns={6}>
            <AddressFieldSet name="address" label="Street Address"/>
          </FormBlock>
        </FormSection>
      }

      {auth.can('sites:write') &&
        <FormSection
          name="Location"
          description={`Set where the ${termFor('site')} is located`}
          columns={6}
        >
          {/* Time Zone */}
          <FormBlock columns={6} extraLargeColumns={3}>
            <SelectField
              name="timezone"
              label="Time Zone *"
              options={timezoneOptions}
            />
          </FormBlock>

          {/* Area */}
          <FormBlock columns={6}>
            {form => (
              <>
                <FieldLabel>Site Area *</FieldLabel>
                <div className='flex flex-col gap-3 mt-1'>
                  <div className='flex flex-col gap-3'>
                    <MapEditor
                      height="32rem"
                      tools={{polygon: true}}
                      features={siteAreaFeatures(site?.uuid, form.values.area)}
                      onFeaturesChanged={allFeatures => handleFeaturesChanged(allFeatures, form)}
                      singleFeature
                      showSearchBox
                    />
                    <p className="text-sm text-gray-500 flex items-center">
                      <PencilIcon className="text-gray-600 h-4 w-4 mr-1"/>
                      Draw the {termFor('site')} area on the map using the drawing tool
                    </p>

                    {/* @ts-ignore */}
                    {(!!form.errors.area?.coordinates && form.touched.area) &&
                      <FieldHelp isError>
                        <div className="flex items-center gap-2">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                          {/* @ts-ignore */}
                          {form.errors.area.coordinates}
                        </div>
                      </FieldHelp>
                    }
                  </div>
                </div>
              </>
            )}
          </FormBlock>
        </FormSection>
      }

      {auth.can('sites:write') &&
        <FormSection
          name="Levels"
          description={`The different vertical parts of this ${termFor('site')}`}
          columns={6}
        >
          <FormBlock columns={6}>
            <LevelListField name="levels" areaName="area"/>
          </FormBlock>
        </FormSection>
      }

      {(auth.can('sites:write') || auth.can('site-hours:write')) &&
        <FormSection
          name="Opening Hours"
          description={`When the ${termFor('site')} is open for the public to visit`}
          columns={6}
        >
          <FormBlock columns={6}>
            <OpeningHoursFieldSet name="hours"/>
          </FormBlock>
        </FormSection>
      }

      {auth.can('sites:write') &&
        <FormSection
          name="Welcome"
          description={`Set up the welcome people receive when they visit this ${termFor('site')}`}
          columns={6}
        >
          {/* Welcome Text */}
          <FormBlock columns={3}>
            {form => (
              <TextField
                name="welcome"
                label="Welcome Text"
                hint="255 chars max"
                placeholder={`Welcome to ${form.values.name || `the ${termFor('site')}`}!`}
                autoComplete="off"
                maxLength={255}
              />
            )}
          </FormBlock>

          {/* Image */}
          <FormBlock columns={6}>
            <MediaField
              name="image_id"
              label="Welcome Image"
              hint="Optional"
              files={site?.image}
              maxFiles={1}
              type={MediaField.types.IMAGE}
            />
          </FormBlock>
        </FormSection>
      }

      {auth.can('sites:write') &&
        <FormSection
          name="Visitor Information"
          description={`Things that visitors to this ${termFor('site')} need to know about`}
          columns={6}
        >
          {/* Information Sections */}
          <FormBlock columns={6}>
            <InformationListField name="visitor_information" canLink/>
          </FormBlock>
        </FormSection>
      }
    </Form>
  )
}

export default SiteForm;